import { useId } from "react"
import { Checkbox, Label } from "react-aria-components"
import type { LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"

export interface CheckboxDivItem {
    value: string
    label: string
    Icon?: LucideIcon
    defaultChecked?: boolean
}

export interface CheckboxDivProps {
    items: CheckboxDivItem[]
    columns?: number
    className?: string
    itemClassName?: string
    labelClassName?: string
    onSelectionChange?: (selectedValues: Set<string>) => void
    selectedValues?: Set<string>
}

const gridColsClassMap: Record<number, string> = {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    5: "grid-cols-5",
    6: "grid-cols-6",
}

export function CheckboxDiv({
    items,
    columns = 2,
    className,
    itemClassName,
    labelClassName,
    onSelectionChange,
    selectedValues,
}: CheckboxDivProps) {
    const id = useId()
    const gridColsClass = gridColsClassMap[columns] || "grid-cols-2"

    return (
        <div className={cn("grid gap-3", gridColsClass, className)}>
            {items.map((item) => {
                const isSelected = selectedValues
                    ? selectedValues.has(item.value)
                    : item.defaultChecked

                return (
                    <div
                        key={`${id}-${item.value}`}
                        className={cn(
                            "relative flex cursor-pointer flex-col gap-4 rounded-md border p-4 shadow-xs outline-none transition-all",
                            isSelected
                                ? "border-2 border-primary/50 bg-primary/5"
                                : "border border-input bg-background",
                            itemClassName
                        )}
                    >
                        <div className="flex justify-between gap-2">
                            <Checkbox
                                id={`${id}-${item.value}`}
                                value={item.value}
                                className="order-1 after:absolute after:inset-0"
                                defaultSelected={item.defaultChecked}
                                isSelected={isSelected}
                                onChange={(isSelected) => {
                                    if (onSelectionChange && selectedValues) {
                                        const newSelection = new Set(selectedValues)
                                        if (isSelected) {
                                            newSelection.add(item.value)
                                        } else {
                                            newSelection.delete(item.value)
                                        }
                                        onSelectionChange(newSelection)
                                    }
                                }}
                            />
                            {item.Icon && (
                                <item.Icon className="opacity-60" size={16} aria-hidden="true" />
                            )}
                        </div>
                        <Label
                            htmlFor={`${id}-${item.value}`}
                            className={cn("cursor-pointer", labelClassName)}
                        >
                            {item.label}
                        </Label>
                    </div>
                )
            })}
        </div>
    )
}
